import React from 'react';
import { Link } from 'react-router-dom';

const gridFloatBreakpoint = 768;
const empty_field = "■";

const empty_key_message = empty_field + " denotes no data available";


const mediaQuery = function(mobileContent, desktopContent) {
  return window.innerWidth < gridFloatBreakpoint ? mobileContent : desktopContent;
};

const sortFcdbFoodId = function(a, b, order) {
  a = a.recordid;
  b = b.recordid;
  if (order === 'desc') {
    // compare first char
    if (b.charAt(0) < a.charAt(0)) { return -1; }
    if (b.charAt(0) > a.charAt(0)) { return 1; }
    // if first char is same, compare following number
    return Number(b.substring(1)) - Number(a.substring(1));
  } else {
    // compare first char
    if (a.charAt(0) < b.charAt(0)) { return -1; }
    if (a.charAt(0) > b.charAt(0)) { return 1; }
    // if first char is same, compare following number
    return Number(a.substring(1)) - Number(b.substring(1));
  }
};

const sortNutrient = function(a, b, order) {
  a = a.ordering_number;
  b = b.ordering_number;

  if (order === 'desc') {
    if (b < a) { return -1; }
    if (b > a) { return 1; }
    return 0;
  } else {
    if (a < b) { return -1; }
    if (a > b) { return 1; }
    return 0;
  }
};

const alphaSortNutrient = function(a, b, order) {
  a = a.display_name;
  b = b.display_name;

  if (order === 'desc') {
    if (b < a) { return -1; }
    if (b > a) { return 1; }
    return 0;
  } else {
    if (a < b) { return -1; }
    if (a > b) { return 1; }
    return 0;
  }
};


const sortFood = function(a, b, order) {
  a = a.shortname;
  b = b.shortname;
  if (order === 'desc') {
    if (b < a) { return -1; }
    if (b > a) { return 1; }
    return 0;
  } else {
    if (a < b) { return -1; }
    if (a > b) { return 1; }
    return 0;
  }
};

// when the value is 'ND', should be sorted below 0
const replaceNaN = function(num) {
  return isNaN(num) ? -1 : num;
};

const sortNumberString = function(a, b, order, field) {
  const numA = replaceNaN(Number(a[field]));
  const numB = replaceNaN(Number(b[field]));
  if (order === 'desc') {
    return numB - numA;
  } else {
    return numA - numB;
  }
};

const renderShowsTotal = function(start, to, total) {
  start = start.toLocaleString('en');
  to = to.toLocaleString('en');
  total = total.toLocaleString('en');
  return (
    <p className="showsTotal">
      Showing {start}-{to} of {total} foods
    </p>
  );
};

const linkFoodDetail = function(cell, row, selectedReport) {
  const report = selectedReport ? `/${selectedReport}` : '';
  return <Link to={`/search/food/${row.recordid}${report}`}>{cell}</Link>;
};

// validate comma-separated strings of recordids from url query: check if it exists in foods.
// returns an array of food objects.
const getValidFoods = function(recordidString, foods) {
  const recordids = recordidString ? recordidString.toUpperCase().split(',') : [];
  return recordids.map(recordid => foods.find(food => food.recordid === recordid)).filter(n => n);
};

// validate 'code' string from url query: check if it exists in components.
// if not, remove that code from the array.
const getValidCodes = function(codesString, components) {
  const queryCodes = codesString ? codesString.toUpperCase().split(',') : [];
  const allCodes = components.map(component => component.code.toUpperCase());
  return queryCodes.map(
    queryCode => allCodes.includes(queryCode) ? queryCode : undefined
  ).filter(n => n);
};

const getValidGroup = function(group) {
  if (!group) { return ''; }

  let upperCaseGroup = group.toUpperCase();
  return /^[A-Z]$/.test(upperCaseGroup) ? upperCaseGroup : '';
};

const getValidReport = function(report) {
  return componentSets.map(componentSet => componentSet.key).includes(report) ? report : 'nip';
};

const componentSets = [
  {key: 'nip', title: 'Nutritional Information Panel (NIP)'},
  {key: 'claimable', title: 'Potential Nutrient Claims'},
  {key: 'basic', title: 'Basic Report'},
  {key: 'full-alphabetical', title: 'Detailed Report (alphabetical)'},
  {key: 'full-grouped', title: 'Detailed Report (by group)'},
];

const nipComponentList = [
  {code: 'ENERC_FSANZ2'},
  {code: 'PROT'},
  {code: 'FAT'},
  {code: 'FASAT', indent: true},
  {code: 'CHOAVL_FSANZ'},
  {code: 'SUGAR', indent: true},
  {code: 'FIBTG'},
  {code: 'NA'},
];

const addIndentToNip = (componentSet) => {
  return componentSet.components.map(component => {
    const list = nipComponentList.find(list => list.code === component.code);
    component.indent = list.indent;
    return component;
  });
};

const createSubheaderRow = (subheader, level) => [{
  category: subheader,
  nutrient: subheader,
  subheader: true,
  level: level,
}];

const createRowData = (rowData) => {
  const {component, units, commponentOfTheFood, claimText, full} = rowData;
  return {
    nutrient: full ? component.shortname : component.display_name, // full report shows shortname
    indent: component.indent,
    unit: units.find(unit => unit._id === component.unit_id).abbreviation
      .replace(/\/100g/, '').replace(/100g/, '100 g'), // second replace gives a space to 'Exchanges per 100g'
    qps: commponentOfTheFood ? commponentOfTheFood.amount : empty_field,
    rdi: commponentOfTheFood ? commponentOfTheFood.percent_RDI : '',
    qpg: commponentOfTheFood ? commponentOfTheFood.value : empty_field,
    claim: claimText ? claimText : '',
  };
};

const createRowsData = (rowsData) => {
  const {componentSet, foodComponents, units, claimTexts, full} = rowsData;
  return componentSet.map(component => {
    const commponentOfTheFood = foodComponents.find(commponentOfTheFood =>
      commponentOfTheFood.component_code === component.code
    );
    const claimText = claimTexts && claimTexts.claimables && claimTexts.claimables[component.code]
      ? claimTexts.claimables[component.code].join(', ')
      : "";
    return createRowData({component, units, commponentOfTheFood, claimText, full});
  });
};

const createNutrientRows = (componentSet, foodComponents, units) => {
  return createRowsData({componentSet, foodComponents, units});
};

const createClaimableNipRows = (componentSet, foodComponents, units, claimTexts) => {
  return createRowsData({componentSet, foodComponents, units, claimTexts});
};

const createFullNutrientRows = (componentSet, foodComponents, units) => {
  return createRowsData({componentSet, foodComponents, units, full: true});
};

const createMultiFoodRows = (componentSet, selectedFoods, units, per100g, full) => {

  return componentSet.map(component => {
    // create an array of the component value of each food
    const commponentOfTheFoods = selectedFoods.map(food =>
      food.components.find(foodComponent => foodComponent.component_code === component.code)
    );
    // add nutrient and unit to tableRowData
    const tableRowData = {
      nutrient: full ? component.shortname : component.display_name, // full report shows shortname
      indent: component.indent,
      unit: units.find(unit => unit._id === component.unit_id).abbreviation
        .replace(/\/100g/, '').replace(/100g/, '100 g'), // second replace gives a space to 'Exchanges per 100g'
    };
    // add foods component values to tableRowData
    commponentOfTheFoods.forEach((foodComponent, i) => {
      tableRowData[`food${i + 1}`] = foodComponent ? (per100g ? foodComponent.value : foodComponent.amount): empty_field;
    });
    return tableRowData;
  });
};

const createMultiFoodFullRows = (componentSet, selectedFoods, units, per100g) => {
  const full = true;
  return createMultiFoodRows(componentSet, selectedFoods, units, per100g, full);
};


export { mediaQuery, sortFcdbFoodId, sortNutrient,alphaSortNutrient, sortFood, replaceNaN, sortNumberString,
    renderShowsTotal, linkFoodDetail, getValidFoods, getValidCodes, getValidGroup,
    getValidReport, componentSets, nipComponentList, addIndentToNip, createSubheaderRow,
    createRowData, createNutrientRows, createClaimableNipRows, createFullNutrientRows,
    createMultiFoodRows, createMultiFoodFullRows, empty_field, empty_key_message
};



// WEBPACK FOOTER //
// ./src/components/util/util.js